


/**
   * Creates a dom element with the given name as a tag.
   *
   * The properties object can entail the following:
   *  - every property that is directly set
   *  - a property 'classlist' that:
   *    - defines an array of classes that are added to the element or
   *    - defines a string with a single class in it
   *  - a property 'parent' that is used to append the created object to the parent
   *  - a property 'attributes' that defines an object where the key is the name of the attribute and the value the value that it should be set to:
   *    example: attributes: { value: 'test' } triggers element.setAttribute('value', 'test');
   *
   * @param {String} name The specified name that will be used as the tag for the element
   * @param {Object} properties An object of properties as specified above
   * @returns {Object} The created dom element
   */
function createDomElement(name, properties) {
  let el = document.createElement(name),
      key, attributeKey, p, i, len;

  for (key in properties) {
    p = properties[key];

    if (key === 'classList') {
      if (Array.isArray(p)) {
        for (i = 0, len = p.length; i < len; i++)
          el.classList.add(p[i]);
      } else {
        el.classList.add(p);
      }
    } else if (key === 'parent') {
      p.appendChild(el);
    } else if (key === 'attributes') {
      for (attributeKey in p)
        el.setAttribute(attributeKey, p[attributeKey]);
    } else {
      if (p !== undefined && p !== null) el[key] = p;
    }
  }

  return el;
}

/**
* Sets an event listener for the given element (element) which listens at the given port (listener).
* Triggers either a parameter update or a settings update, depending on the options of the controls initialization.
*
* The menuEl.valueConversion can either be:
*  - the name of the property that should be used,
*  - a callback which returns the value (input to that callback is the menu element and the element itself) or
*  - an object with:
*    - the 'update' property as a string, the property that is chosen is solely used to update the value of the elements in the menu
*    - the 'f' property as a function that returns the value for further processing
*    - an optional 'i' property as a function that returns the value for updating the control based on an external change of the setting's or parameter's value
*
* A timeout can be defined to make delayed updates possible
*
* @param {Object} API The viewer api
* @param {String} id Either the id of the parameter or the name of the setting
* @param {Object} menuEl The whole element
* @param {element} obj The specific element (child or sub-child of menuEl) to which to assign the listener
* @param {String} listener The value to listen to
* @param {Function} update The function to call to update the menu elements
* @param {Number} [timeoutInterval] An optional number that delays the update and resets it if another event occured
* @param {Function} parameterValidation Passes values by reference after ui update but before parameter change to backend.
* @return {Object} the event listener as [listener, handler] such as ['click', function () { }]
*/
function setDomElementEventListener(API, id, menuEl, element, listener, update, timeoutInterval, parameterValidation) {
  let timeout,
      interval = timeoutInterval || 0,
      val1, val2,
      handler;

  // listen for an event to occur
  handler = function (event) {

    if (!API)
      _destroy();

    // get the value
    if (menuEl.valueConversion.update && menuEl.valueConversion.f) {
      val1 = element[menuEl.valueConversion.update];
      val2 = menuEl.valueConversion.f(menuEl, element, event);
    } else {
      if (typeof menuEl.valueConversion === 'string') {
        val1 = val2 = element[menuEl.valueConversion];
      } else {
        val1 = val2 = menuEl.valueConversion(menuEl, element, event);
      }
    }

    // update the menu element, check if update got accepted
    if (!update(val1, event)) {
      return;
    }

    // clear the timeout if there was one
    clearTimeout(timeout);

    // set a new timeout (with 0 if no interval was defined) and update parameter/setting when done
    timeout = setTimeout(function () {

      let valid = true;
      if (typeof parameterValidation === 'function') {
        let valObj = { val1: val1, val2: val2 };
        valid = parameterValidation(valObj, event);
        val1 = valObj.val1;
        val2 = valObj.val2;
      }

      if (!valid) {
        return;
      }

      if (menuEl.parameter === true) {
        if (menuEl.parametersQueue && menuEl.parametersQueue.use) {
          menuEl.parametersQueue.add(menuEl, id, val2);
        }
        else {
          API.parameters.updateAsync({ id: id, value: val2 });
          menuEl.setStackToLast();
        }
      } else if (menuEl.export === true) {
        API.exports.requestAsync({ id: id });
      } else {

        if (typeof menuEl.customUpdate === 'function') {
          menuEl.customUpdate(id, val2, event, menuEl);
        } else {
          API.updateSettingAsync(id, val2);
          menuEl.setStackToLast();
        }

      }
    }, interval);
  };

  element.addEventListener(listener, handler);
  return handler;
}

let _isMobile = false;
/* eslint-disable */
(function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) _isMobile = true; })(navigator.userAgent || navigator.vendor || window.opera);
/* eslint-enable */
function isMobileDevice() {
  return _isMobile;
}

let _isMobileOrTable = false;
(function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) _isMobileOrTable = true; })(navigator.userAgent || navigator.vendor || window.opera);
function isMobileOrTabletDevice() {
  return _isMobileOrTable;
};

let _isIPadOrIPhone = false;
// see https://stackoverflow.com/questions/58019463/how-to-detect-device-name-in-safari-on-ios-13-while-it-doesnt-show-the-correct
_isIPadOrIPhone = (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && !window.MSStream;
function isIPadOrIPhone() {
   return _isIPadOrIPhone;
}


function isEventSupported(eventName, tagName) {
  var TAGNAMES = {
    'select': 'input', 'change': 'input',
    'submit': 'form', 'reset': 'form',
    'error': 'img', 'load': 'img', 'abort': 'img'
  };

  var tag = tagName || TAGNAMES;
  var el = document.createElement(tag || 'div');
  eventName = 'on' + eventName;
  var isSupported = (eventName in el);
  if (!isSupported) {
    el.setAttribute(eventName, 'return;');
    isSupported = typeof el[eventName] == 'function';
  }
  el = null;
  return isSupported;
}

function domSupportsHtml5Color() {
  try {
    colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = '!';

    return (colorInput.type === 'color' && colorInput.value !== '!');
  } catch (e) {
    return false;
  }
}

module.exports.createDomElement = createDomElement;
module.exports.setDomElementEventListener = setDomElementEventListener;
module.exports.isMobileDevice = isMobileDevice;
module.exports.isMobileOrTabletDevice = isMobileOrTabletDevice;
module.exports.isEventSupported = isEventSupported;
module.exports.domSupportsHtml5Color = domSupportsHtml5Color;
module.exports.isIPadOrIPhone = isIPadOrIPhone;
